// useAuth：把 CognitoAuth + AuthedFetchClient 接入 React 的最小 hook。
//
// 职责（保持最小，不越界其他任务）：
//  - 首次挂载时若处于 OAuth 回调（URL 含 code）则换取 token 并清理 URL。
//  - 暴露 login / logout / authedFetch 与认证状态。
//  - 注册重认证回调：会话过期时记录待提交请求数并提示，可一键重新登录。
//
// Requirements: 2.1, 2.2, 2.6, 11.4。

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CognitoAuth } from "./cognito.js";
import { AuthedFetchClient, type PendingRequest } from "./authedFetch.js";

export interface AuthState {
  /** 配置是否齐备（缺失时仅提示，不崩溃）。 */
  configured: boolean;
  /** 是否已认证（持有未过期 access token）。 */
  authenticated: boolean;
  /** 回调换取 token 进行中。 */
  loading: boolean;
  /** 最近一次错误信息（登录失败 / token 交换失败）。 */
  error: string | null;
  /** 因会话过期被保留、待重放的请求数。 */
  pendingCount: number;
}

export interface UseAuthResult extends AuthState {
  login: () => void;
  logout: () => void;
  /** 带认证的 fetch（自动附带 Bearer token）。 */
  authedFetch: (input: string, init?: RequestInit) => Promise<Response>;
  /** 重新登录（保留待提交请求）。 */
  reauthenticate: () => void;
}

export function useAuth(): UseAuthResult {
  // 单例：整个应用生命周期共享一份 auth / client。
  const authRef = useRef<CognitoAuth | null>(null);
  if (!authRef.current) authRef.current = new CognitoAuth();
  const auth = authRef.current;

  const clientRef = useRef<AuthedFetchClient | null>(null);
  if (!clientRef.current) clientRef.current = new AuthedFetchClient(auth);
  const client = clientRef.current;

  const [state, setState] = useState<AuthState>(() => ({
    configured: auth.isConfigured(),
    authenticated: auth.isAuthenticated(),
    loading: false,
    error: null,
    pendingCount: 0,
  }));

  const refreshState = useCallback(
    (patch: Partial<AuthState> = {}) => {
      setState((prev) => ({
        ...prev,
        configured: auth.isConfigured(),
        authenticated: auth.isAuthenticated(),
        pendingCount: client.getPending().length,
        ...patch,
      }));
    },
    [auth, client],
  );

  // 注册重认证回调：会话过期时更新待提交计数并标记未认证。
  useEffect(() => {
    client.onReauthRequired((pending: readonly PendingRequest[]) => {
      refreshState({
        authenticated: false,
        pendingCount: pending.length,
        error: "会话已过期，请重新登录",
      });
    });
  }, [client, refreshState]);

  // 首次挂载：处理 OAuth 回调（若 URL 含 code）。
  const handledCallback = useRef(false);
  useEffect(() => {
    if (handledCallback.current) return;
    handledCallback.current = true;

    const search = typeof window !== "undefined" ? window.location.search : "";
    if (!CognitoAuth.isCallbackUrl(search)) {
      refreshState();
      return;
    }
    setState((p) => ({ ...p, loading: true }));
    auth
      .handleRedirectCallback(search)
      .then(async () => {
        // 清理 URL 上的 code/state，避免刷新重复换取。
        if (typeof window !== "undefined" && window.history?.replaceState) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        refreshState({ loading: false, error: null });
        // 登录成功后重放待提交请求（Req 11.4）。
        await client.replayPending();
        refreshState();
      })
      .catch((err: unknown) => {
        refreshState({
          loading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      });
  }, [auth, client, refreshState]);

  const login = useCallback(() => {
    void auth.login().catch((err: unknown) => {
      refreshState({ error: err instanceof Error ? err.message : String(err) });
    });
  }, [auth, refreshState]);

  const logout = useCallback(() => {
    auth.logout();
    refreshState({ authenticated: false });
  }, [auth, refreshState]);

  const reauthenticate = useCallback(() => {
    // 保留待提交请求并跳转登录。
    login();
  }, [login]);

  const authedFetch = useCallback(
    (input: string, init?: RequestInit) => client.fetch(input, init),
    [client],
  );

  return useMemo(
    () => ({ ...state, login, logout, authedFetch, reauthenticate }),
    [state, login, logout, authedFetch, reauthenticate],
  );
}
